<script setup lang="ts">
import { computed } from "vue";
import {
  conditionDisplayNames,
  statusConditionDisplayNames,
  loginAccountConditionDisplayNames,
  timeFrameConditionDisplayNames,
} from "~/utils/enumDisplayNames";
import {
  StatusBasedConditionType,
  FormElementConditionType,
  FormElementRuleType,
  RuleConditionType,
  LoginAccountRuleType,
  TimeFrameRuleType,
} from "~/api";
import { useOrgInfoStore } from "~/stores/OrgInfoStore";

const props = defineProps<{
  rules: {
    guid: string;
    conditionType: RuleConditionType;
    formRuleGuid: string;
    ruleType: FormElementRuleType;
  }[];
  dependentRules: {
    ruleType: FormElementRuleType;
    affectedQuestionCode: string;
    conditionType: RuleConditionType;
    answerValue?: string;
  }[];
}>();

const elementRules = computed(() => props.rules);
const dependentRules = computed(() => props.dependentRules);

const orgInfoStore = useOrgInfoStore();

const getOrgGroupName = (orgGroupGuid: string) =>
  orgInfoStore.MyOrgInfo?.orgGroupDtos.find((og) => og.guid === orgGroupGuid)
    ?.name || "";
</script>

<template>
  <div class="ElementRulesEditor">
    <div v-if="elementRules.length > 0">
      <el-table :data="elementRules" style="width: 100%" empty-text="No Rules">
        <el-table-column prop="ruleType" label="Rule Type" width="120" />
        <el-table-column label="" width="40">
          <template #default>if</template>
        </el-table-column>
        <el-table-column label="Condition">
          <template #default="{ row }">
            <template v-if="row.conditionType === 'FormElementBased'">
              {{ row.questionCode }}
              {{ conditionDisplayNames[row.type as FormElementConditionType] }}
              <span v-if="row.answerValue !== null"
                >"{{ row.answerValue }}"</span
              >
            </template>
            <template v-else-if="row.conditionType === 'FormStatusBased'">
              Form
              {{
                statusConditionDisplayNames[
                  row.type as StatusBasedConditionType
                ]
              }}
              "{{ row.formStatus }}"
            </template>
            <template v-else-if="row.conditionType === 'WorkflowStatusBased'">
              Workflow
              {{
                statusConditionDisplayNames[
                  row.type as StatusBasedConditionType
                ]
              }}
              "{{ row.workflowStatus }}"
            </template>
            <template v-else-if="row.conditionType === 'LoginAccountBased'">
              User
              {{
                loginAccountConditionDisplayNames[
                  row.type as LoginAccountRuleType
                ]
              }}
              {{ row.role ? row.role : getOrgGroupName(row.orgGroupGuid) }}
            </template>
            <template v-else-if="row.conditionType === 'TimeFrameBased'">
              Date
              {{
                timeFrameConditionDisplayNames[row.type as TimeFrameRuleType]
              }}
              {{ row.timeFrameCode }}
            </template>
            <template v-else> Unknown Condition </template>
          </template>
        </el-table-column>
      </el-table>
    </div>
    <div v-if="dependentRules.length > 0">
      <el-table
        :data="dependentRules"
        style="width: 100%"
        empty-text="No Dependencies"
      >
        <el-table-column
          prop="affectedQuestionCode"
          label="Affected Question"
          width="170"
        />
        <el-table-column label="Rule">
          <template #default="{ row }">
            If {{ conditionDisplayNames[row.type as FormElementConditionType] }}
            <span v-if="row.answerValue !== null">"{{ row.answerValue }}"</span>
            set {{ row.affectedQuestionCode }} to {{ row.ruleType }}
          </template>
        </el-table-column>
      </el-table>
    </div>
    <div v-else class="el-table__empty-text centre">No Rules</div>
  </div>
</template>

<style lang="less">
.ElementRulesEditor {
  .centre {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
  }
}
</style>
